import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiLink, FiRefreshCw } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { readStoredUser } from "../../../utils/sessionUser";
import PosItemLinkEditor, { posLinkUnit } from "./PosItemLinkEditor";
import PosSalesTab from "./PosSalesTab";
import "./PosCatalogTab.css";

const COPY = {
  es: {
    title: "Artículos del POS", description: "Consulta los artículos recibidos de tus puntos de venta.",
    reload: "Recargar", search: "Buscar por código, nombre o POS", code: "Código", name: "Nombre",
    source: "POS de origen", received: "Última recepción", loading: "Cargando artículos del POS…",
    empty: "Todavía no se han recibido artículos del POS.", noResults: "No hay artículos que coincidan con la búsqueda.",
    failed: "No se han podido cargar los artículos del POS. Vuelve a intentarlo.",
    denied: "No tienes permiso para consultar los artículos del POS.", noUnit: "Selecciona una unidad de negocio para consultar los artículos del POS.",
    count: "artículos recibidos", items: "Artículos del POS", sales: "Ventas POS", active: "Activo", inactive: "Inactivo", state: "Estado",
    salesDescription: "Consulta las ventas recibidas de tus puntos de venta.",
    failedRefresh: "No se han podido actualizar los artículos. Se muestran los últimos datos recibidos.", automatic: "Actualización automática",
    restrictedLink: "Vínculo restringido",
    link: "Vinculación en Compras", unlinked: "Sin vincular", item: "Artículo", recipe: "Receta", actions: "Acciones",
    configure: "Vincular", change: "Cambiar vínculo", unitMissing: "Unidad no indicada", perSale: "por unidad vendida",
    saved: "Vinculación guardada.", removed: "Vinculación eliminada.",
    editor: "Vincular artículo del POS", purchasingTarget: "Destino en Compras", targetType: "Tipo de destino",
    chooseTarget: "Elegir artículo o receta", chooseItem: "Elegir artículo", chooseRecipe: "Elegir receta",
    searchTarget: "Buscar por nombre o código", noTargets: "No hay destinos que coincidan con la búsqueda.",
    optionsLoading: "Cargando artículos, recetas y almacenes…", optionsFailed: "No se han podido cargar los artículos, recetas y almacenes de Compras.",
    inventoryLocation: "Almacén del que descontar", chooseLocation: "Elegir almacén", searchLocation: "Buscar almacén por nombre o código",
    noLocations: "No hay almacenes activos que coincidan con la búsqueda.", locationHint: "Almacén para los descuentos de inventario de esta vinculación.",
    locationRequired: "Elige el almacén del que descontar antes de guardar el vínculo.", locationMissing: "Almacén sin configurar",
    locationUnavailable: "El almacén guardado ya no está disponible. Elige un almacén activo.",
    locationTargetFirst: "Elige primero el artículo o receta de Compras para consultar sus almacenes.",
    refreshStock: "Actualizar existencias", stockLoading: "Consultando existencias…", stockRestricted: "No tienes permiso para consultar existencias. Puedes elegir un almacén sin ver sus cantidades.",
    stockFailed: "No se han podido consultar las existencias. Las cantidades son desconocidas; puedes reintentar o elegir un almacén.", stockUnknown: "Existencias: —", stockUpdated: "Actualizado",
    stockFor: "Stock de", stockItem: "Existencias del artículo de Compras, en su unidad principal.",
    stockIngredients: "Existencias de los ingredientes inventariables, no del plato preparado. Cantidades en la unidad principal de cada artículo.",
    stockManufactured: "Existencias del artículo fabricado, en su unidad principal.", stockUnavailable: "Existencias no disponibles para este destino.",
    stockDirectConsumption: "Los artículos de consumo directo no mantienen existencias de inventario.", stockRecipeEmpty: "La receta no contiene artículos inventariables.",
    stockRecipeUnresolved: "La receta contiene equivalencias sin resolver; no se puede consultar su inventario completo.",
    stockManufacturedMissing: "No se ha encontrado el artículo fabricado de esta receta.", stockManufacturedUnitMismatch: "Las unidades de la receta y su artículo fabricado no coinciden.",
    stockLocationNote: "Stock registrado por ubicación; el stock sin ubicación asignada no aparece aquí.",
    currentLink: "Vínculo actual", targetUnavailable: "El destino actual no está disponible. Elige otro destino o quita el vínculo.",
    quantity: "Cantidad por unidad vendida", unit: "Unidad de Compras", quantityHint: "Cantidad de este artículo o receta correspondiente a una unidad vendida en el POS.",
    chooseUnit: "Elegir unidad de medida", searchUnit: "Buscar unidad de medida", noUnits: "No hay unidades que coincidan con la búsqueda.",
    unitUnavailable: "La unidad guardada ya no está disponible. Elige una unidad configurada del artículo.",
    unitChanged: "La conversión del artículo ha cambiado. Cierra y vuelve a abrir el vínculo para revisar la cantidad.",
    invalidQuantity: "Introduce una cantidad mayor que 0, menor que 100.000.000 y con un máximo de 6 decimales.",
    save: "Guardar vínculo", saving: "Guardando…", cancel: "Cancelar", close: "Cerrar", back: "Volver",
    unlink: "Quitar vínculo", confirmUnlink: "Confirmar desvinculación", removing: "Quitando vínculo…",
    unlinkHelp: "Se eliminará sólo la correspondencia. El artículo del POS y el artículo o receta de Compras se conservarán.",
    mutationFailed: "No se ha podido guardar el cambio. Revisa la conexión e inténtalo de nuevo.",
    sessionChanged: "La unidad de negocio o los permisos han cambiado. Vuelve a abrir el artículo.",
  },
  en: {
    title: "POS items", description: "View the items received from your points of sale.",
    reload: "Reload", search: "Search by code, name or POS", code: "Code", name: "Name",
    source: "Source POS", received: "Last received", loading: "Loading POS items…",
    empty: "No POS items have been received yet.", noResults: "No items match your search.",
    failed: "Could not load POS items. Please try again.",
    denied: "You do not have permission to view POS items.", noUnit: "Select a business unit to view POS items.",
    count: "items received", items: "POS items", sales: "POS sales", active: "Active", inactive: "Inactive", state: "Status",
    salesDescription: "View sales received from your points of sale.",
    failedRefresh: "Could not refresh the items. The last received data is displayed.", automatic: "Automatic updates",
    restrictedLink: "Restricted link",
    link: "Purchasing link", unlinked: "Not linked", item: "Item", recipe: "Recipe", actions: "Actions",
    configure: "Link", change: "Change link", unitMissing: "Unit not specified", perSale: "per unit sold",
    saved: "Link saved.", removed: "Link removed.",
    editor: "Link POS item", purchasingTarget: "Purchasing target", targetType: "Target type",
    chooseTarget: "Choose item or recipe", chooseItem: "Choose item", chooseRecipe: "Choose recipe",
    searchTarget: "Search by name or code", noTargets: "No targets match your search.",
    optionsLoading: "Loading items, recipes and warehouses…", optionsFailed: "Could not load Purchasing items, recipes and warehouses.",
    inventoryLocation: "Inventory location to deduct from", chooseLocation: "Choose warehouse", searchLocation: "Search warehouses by name or code",
    noLocations: "No active warehouses match your search.", locationHint: "Warehouse for this link’s inventory deductions.",
    locationRequired: "Choose the inventory location to deduct from before saving the link.", locationMissing: "Warehouse not configured",
    locationUnavailable: "The saved warehouse is no longer available. Choose an active warehouse.",
    locationTargetFirst: "Choose the Purchasing item or recipe first to view its warehouses.",
    refreshStock: "Refresh stock", stockLoading: "Loading stock…", stockRestricted: "You do not have permission to view stock. You can choose a warehouse without viewing quantities.",
    stockFailed: "Could not load stock. Quantities are unknown; you can retry or choose a warehouse.", stockUnknown: "Stock: —", stockUpdated: "Updated",
    stockFor: "Stock for", stockItem: "Stock of the Purchasing item, in its main unit.",
    stockIngredients: "Stock of inventory ingredients, not the prepared dish. Quantities use each item's main unit.",
    stockManufactured: "Stock of the manufactured item, in its main unit.", stockUnavailable: "Stock is not available for this target.",
    stockDirectConsumption: "Direct-consumption items do not hold inventory stock.", stockRecipeEmpty: "The recipe contains no inventory items.",
    stockRecipeUnresolved: "The recipe contains unresolved equivalents; its full inventory cannot be checked.",
    stockManufacturedMissing: "The manufactured item for this recipe was not found.", stockManufacturedUnitMismatch: "The recipe and its manufactured item have different units.",
    stockLocationNote: "Recorded stock by location; stock without an assigned location is not shown here.",
    currentLink: "Current link", targetUnavailable: "The current target is unavailable. Choose another target or remove the link.",
    quantity: "Quantity per unit sold", unit: "Purchasing unit", quantityHint: "Quantity of this item or recipe corresponding to one unit sold in the POS.",
    chooseUnit: "Choose unit of measure", searchUnit: "Search units of measure", noUnits: "No units match your search.",
    unitUnavailable: "The saved unit is no longer available. Choose a configured item unit.",
    unitChanged: "The item conversion has changed. Close and reopen the link to review its quantity.",
    invalidQuantity: "Enter a quantity greater than 0, below 100,000,000 and with at most 6 decimal places.",
    save: "Save link", saving: "Saving…", cancel: "Cancel", close: "Close", back: "Back",
    unlink: "Remove link", confirmUnlink: "Confirm unlink", removing: "Removing link…",
    unlinkHelp: "Only the link will be removed. The POS item and the Purchasing item or recipe will remain unchanged.",
    mutationFailed: "Could not save the change. Check the connection and try again.",
    sessionChanged: "Your business unit or permissions changed. Reopen the item to continue.",
  },
};
const allowed = (user, action) => Number(user?.id) === 1 || user?.permissions?.[`purchasing.items.${action}`] === true;
const normalize = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const TAB_KEYS = ["items", "sales"];

export default function PosCatalogTab() {
  const { languageId, themeId } = usePmsTheme();
  const copy = COPY[languageId] || COPY.en;
  const [user, setUser] = useState(readStoredUser);
  const [sessionVersion, setSessionVersion] = useState(0);
  const businessUnitId = user?.active_business_unit_id;
  const canView = allowed(user, "view");
  const canEdit = canView && allowed(user, "manage_pos_links");
  const canViewRecipes = Number(user?.id) === 1 || user?.permissions?.["purchasing.recipes.view"] === true;
  const restrictedLink = (link) => link?.restricted === true || (link?.target_type === "recipe" && !canViewRecipes);
  const requestVersion = useRef(0);
  const pendingRequest = useRef(null);
  const mutationLock = useRef(false);
  const tabs = useRef([]);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("items");
  const [editor, setEditor] = useState(null);
  const [mutating, setMutating] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const scope = `${user?.id}:${businessUnitId}:${JSON.stringify(user?.permissions)}:${canView}:${canEdit}:${canViewRecipes}:${sessionVersion}`;

  useEffect(() => {
    const update = (event) => {
      if (event.type === "storage" && event.key && !["palantir_user", "palantir_token"].includes(event.key)) return;
      requestVersion.current += 1;
      pendingRequest.current = null;
      mutationLock.current = false;
      setEditor(null);
      setMutating(false);
      setFeedback(null);
      setUser(readStoredUser());
      setSessionVersion((current) => current + 1);
    };
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", update);
    };
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!businessUnitId || !canView || pendingRequest.current?.scope === scope || mutationLock.current) return;
    const version = ++requestVersion.current;
    pendingRequest.current = { version, scope };
    if (!quiet) setRefreshing(true);
    try {
      const response = await axios.get("/api/purchasing/pos-catalog/items", {
        pilcoreExpectedUserId: user?.id,
        headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) },
      });
      if (version !== requestVersion.current) return;
      const session = readStoredUser();
      if (String(session?.id) !== String(user?.id) || String(session?.active_business_unit_id) !== String(businessUnitId) || !allowed(session, "view") || JSON.stringify(session?.permissions) !== JSON.stringify(user?.permissions)) {
        setUser(session);
        return;
      }
      if (!Array.isArray(response.data?.rows)) throw new Error("Invalid POS item response");
      setRows(response.data.rows);
      setCount(response.data.count ?? response.data.rows.length);
      setLoaded(true);
      setFailed(false);
    } catch (_error) {
      if (version === requestVersion.current) setFailed(true);
    } finally {
      if (pendingRequest.current?.version === version) pendingRequest.current = null;
      if (version === requestVersion.current) { setLoading(false); setRefreshing(false); }
    }
  }, [businessUnitId, canView, scope, user?.id, user?.permissions]);

  useEffect(() => {
    setRows([]); setCount(0); setLoaded(false); setLoading(Boolean(businessUnitId && canView)); setFailed(false);
    setEditor(null); setFeedback(null);
    load();
    if (!businessUnitId || !canView) return undefined;
    const timer = window.setInterval(() => load(true), 5000);
    return () => { window.clearInterval(timer); requestVersion.current += 1; };
  }, [load, businessUnitId, canView]);

  const setMutation = (busy) => {
    mutationLock.current = busy;
    setMutating(busy);
    if (busy) { requestVersion.current += 1; pendingRequest.current = null; setFeedback(null); }
    else setRefreshing(false);
  };
  const saved = (id, link) => {
    setRows((current) => current.map((row) => String(row.id) === String(id) ? { ...row, link } : row));
    setEditor(null);
    setFeedback(link ? "saved" : "removed");
  };
  const openEditor = (row) => {
    const session = readStoredUser();
    if (restrictedLink(row.link) || (row.link?.target_type === "recipe" && Number(session?.id) !== 1 && session?.permissions?.["purchasing.recipes.view"] !== true) || !canEdit || !allowed(session, "view") || !allowed(session, "manage_pos_links") || String(session?.id) !== String(user?.id) || String(session?.active_business_unit_id) !== String(businessUnitId)) return;
    setEditor({ row, scope }); setFeedback(null);
  };
  const selectTab = (event, index) => {
    let next;
    if (event.key === "ArrowLeft") next = (index + TAB_KEYS.length - 1) % TAB_KEYS.length;
    else if (event.key === "ArrowRight") next = (index + 1) % TAB_KEYS.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = TAB_KEYS.length - 1;
    else return;
    event.preventDefault(); setView(TAB_KEYS[next]); tabs.current[next]?.focus();
  };
  const query = normalize(search.trim());
  const visibleRows = rows.filter((row) => [row.code, row.name, row.source_name].some((value) => normalize(value).includes(query)));
  const formatDate = (value) => {
    const date = value ? new Date(value) : null;
    return date && Number.isFinite(date.getTime()) ? date.toLocaleString(languageId === "es" ? "es-ES" : "en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";
  };
  const showLink = (link) => restrictedLink(link) ? copy.restrictedLink : link ? <><strong>{copy[link.target_type]} · {link.target_name || link.target_code || `#${link.target_id}`}</strong>
    <small>{Number(link.quantity).toLocaleString(languageId === "es" ? "es-ES" : "en-GB", { maximumFractionDigits: 6 })} {posLinkUnit(link.unit, copy.unitMissing)} {copy.perSale}</small>
    <small className={!link.inventory_location_id ? "posCatalog-locationMissing" : undefined}>{link.inventory_location_id
      ? `${copy.inventoryLocation}: ${link.inventory_location?.name || `#${link.inventory_location_id}`}${link.inventory_location?.is_active === false ? ` · ${copy.inactive}` : ""}` : copy.locationMissing}</small></> : copy.unlinked;

  return (
    <section className="spx-card posCatalog" aria-label={copy.title} aria-busy={view !== "sales" && loading}>
      <div className="spx-head"><div className="spx-headMain"><h2 className="spx-title">{copy.title}</h2><p className="spx-subtitle">{view === "sales" ? copy.salesDescription : copy.description} {copy.automatic}.</p></div>
        {canView && businessUnitId && view !== "sales" ? <div className="spx-actions"><button type="button" className="spx-btn" onClick={() => load()} disabled={loading || refreshing || mutating}><FiRefreshCw aria-hidden="true" /> {copy.reload}</button></div> : null}
      </div>
      {!canView ? <p className="spx-empty" role="alert">{copy.denied}</p> : !businessUnitId ? <p className="spx-empty">{copy.noUnit}</p> : <>
        <div className="posCatalog-tabs" role="tablist" aria-label={copy.title}>{TAB_KEYS.map((tab, index) => <button key={tab} type="button" role="tab"
          id={`pos-catalog-tab-${tab}`} ref={(node) => { tabs.current[index] = node; }} aria-selected={view === tab} aria-controls="pos-catalog-panel" tabIndex={view === tab ? 0 : -1}
          className={`spx-chip ${view === tab ? "spx-chip--active" : ""}`} onClick={() => setView(tab)} onKeyDown={(event) => selectTab(event, index)}>{copy[tab]}</button>)}</div>
        <div id="pos-catalog-panel" className="posCatalog-panel" role="tabpanel" aria-labelledby={`pos-catalog-tab-${view}`} tabIndex={0}>
          {view === "sales" ? <PosSalesTab key={scope} /> : <>
          {feedback && <p className="posCatalog-feedback" role="status">{copy[feedback]}</p>}
          {loading ? <p className="spx-empty" role="status">{copy.loading}</p> : failed && !loaded ? <p className="spx-banner spx-banner--error" role="alert">{copy.failed}</p> : <>
            {failed && <p className="spx-banner spx-banner--error" role="alert">{copy.failedRefresh}</p>}
            <div className="spx-toolbar"><div className="ifm-toolbarGrid ifm-toolbarGrid--two">
              <input type="search" className="spx-search" aria-label={copy.search} placeholder={copy.search} value={search} onChange={(event) => setSearch(event.target.value)} />
              <span className="spx-note">{count} {copy.count}</span>
            </div></div>
            {!visibleRows.length ? <p className="spx-empty" role="status">{rows.length ? copy.noResults : copy.empty}</p> : <div className="spx-tableWrap">
              <table className="spx-table posCatalog-table" aria-label={copy.items}><thead><tr>{[copy.code, copy.name, copy.source, copy.state, copy.link, copy.received, ...(canEdit ? [copy.actions] : [])].map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
                <tbody>{visibleRows.map((row) => <tr key={row.id}>
                  <td className="spx-code">{row.code || row.external_item_id}</td><td>{row.name}</td><td>{row.source_name || "—"}</td><td>{row.is_active === false ? copy.inactive : copy.active}</td>
                  <td className="posCatalog-link">{showLink(row.link)}</td>
                  <td>{formatDate(row.last_received_at)}</td>
                  {canEdit && <td><button type="button" className="spx-btn" disabled={mutating || restrictedLink(row.link)} onClick={() => openEditor(row)} aria-label={`${row.link ? copy.change : copy.configure}: ${row.name}`}><FiLink aria-hidden="true" />{row.link ? copy.change : copy.configure}</button></td>}
                </tr>)}</tbody>
              </table>
            </div>}
          </>}
          </>}
        </div>
      </>}
      {editor?.scope === scope && canView && canEdit && <PosItemLinkEditor key={`${scope}:${editor.row.id}`} row={editor.row} user={user} themeId={themeId} copy={copy} locale={languageId === "es" ? "es-ES" : "en-GB"}
        onClose={() => setEditor(null)} onBusy={setMutation} onSaved={saved} />}
    </section>
  );
}
